import { useState } from 'react';
import {
	MinusCircleOutlined,
	PlusOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	AutoComplete,
	Button,
	Card,
	Col,
	DatePicker,
	Flex,
	Form,
	Input,
	InputNumber,
	message,
	Progress,
	Result,
	Row,
	Select,
	Space,
	Spin,
	Steps,
	Tag,
	Tooltip,
	Typography,
	Upload,
} from 'antd';
import { useNavigate } from 'react-router';
import type { Dayjs } from 'dayjs';
import {
	useCreateInternalOrderMutation,
	useFinalizeInternalOrderMutation,
	useGetInternalOrderLineItemsQuery,
	useReanalyzeInternalFileMutation,
	useUploadInternalFilesMutation,
} from '../../../redux/api/order';
import { useGetMaterialTypesQuery } from '../../../redux/api/material';
import { INTERNAL_CATEGORIES } from '../../../config/constants';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import { isInternalOrdersManager } from '../../../utils/isInternalOrdersManager';
import type { UploadFile } from 'antd/es/upload/interface';
import { AnalysisRows, analyzedCount, finalizeEnabled } from './AnalysisStep';

const { TextArea } = Input;
const { Text, Title } = Typography;

export interface InternalOrderLineItemFormValues {
	name: string;
	quantity: number;
	material_name?: string;
	finish_name: string;
	coating_name: string;
	required_part_ship_date?: Dayjs;
	required_partner_ship_date?: Dayjs;
	// Upload widgets: multipart-only, never part of the JSON create payload.
	step_file?: UploadFile[];
	drawing_file?: UploadFile[];
}

export interface InternalOrderFormValues {
	internal_category?: string;
	internal_notes?: string;
	line_items: InternalOrderLineItemFormValues[];
}

export function buildInternalOrderPayload(values: InternalOrderFormValues) {
	return {
		internal_category: values.internal_category,
		internal_notes: values.internal_notes,
		line_items: values.line_items.map((li) => ({
			name: li.name,
			quantity: li.quantity,
			material_name: li.material_name || null,
			finish_name: li.finish_name,
			coating_name: li.coating_name,
			required_part_ship_date: li.required_part_ship_date?.format('YYYY-MM-DD'),
			required_partner_ship_date:
				li.required_partner_ship_date?.format('YYYY-MM-DD'),
		})),
	};
}

/** AntD `Upload` inside `Form.Item` hands `onChange` events, not the file list. */
export const normalizeUploadEvent = (e: unknown): UploadFile[] | undefined => {
	if (Array.isArray(e)) return e as UploadFile[];
	return (e as { fileList?: UploadFile[] } | undefined)?.fileList;
};

/** `beforeUpload: () => false` keeps the raw File on `originFileObj`. */
export const extractUploadFile = (value?: UploadFile[]): File | undefined => {
	const first = value?.[0];
	if (!first) return undefined;
	return (first.originFileObj as File | undefined) ?? (first as unknown as File);
};

/**
 * Pairs each submitted form row with the line item the backend created for it.
 * `OrderLineItem` has no default ordering, so the create response is matched by
 * name first (names repeat rarely, and duplicates are consumed in order); rows
 * without a name match fall back to the unconsumed created items in order. The
 * fallback draws from a pool of not-yet-matched items — a bare `created[index]`
 * could hand out an item some earlier row already claimed by name, silently
 * attaching uploads to the wrong line item.
 */
export function matchCreatedLineItems<T extends { id: string; name: string }>(
	formRows: { name: string }[],
	created: T[],
): (T | undefined)[] {
	const byName = new Map<string, T[]>();
	created.forEach((item) => {
		const bucket = byName.get(item.name) ?? [];
		bucket.push(item);
		byName.set(item.name, bucket);
	});
	// Two passes: resolve every name match first, THEN hand out the leftovers
	// positionally. Interleaving the fallback with matching would let an early
	// fallback row take an item a later row still claims by name.
	const nameMatched = formRows.map((row) => byName.get(row.name)?.shift());
	const taken = new Set<T>(nameMatched.filter((m): m is T => m !== undefined));
	const unused = created.filter((item) => !taken.has(item));
	return nameMatched.map((matched) => matched ?? unused.shift());
}

export interface NotReadyRow {
	id: string | null;
	name: string | null;
	reasons: string[];
}

/** Renders the finalize 409 envelope (`{ not_ready: [...] }`) as one line. */
export const notReadyMessage = (error: unknown): string | null => {
	const rows = (error as { data?: { not_ready?: NotReadyRow[] } } | undefined)
		?.data?.not_ready;
	if (!rows || rows.length === 0) return null;
	return rows
		.map((row) => `${row.name ?? 'Order'}: ${row.reasons.join(', ')}`)
		.join(' · ');
};

const AddInternalOrder = () => {
	const navigate = useNavigate();
	const { data: loggedInUser, isLoading: isLoadingUser } = useGetLoggedInUserQuery();
	const { data: materialTypesData } = useGetMaterialTypesQuery({ material_category: null });
	// test harness / error payloads may hand back non-arrays — render an empty picker then
	const materialTypes = Array.isArray(materialTypesData) ? materialTypesData : [];
	const canManage = isInternalOrdersManager(loggedInUser);
	const [form] = Form.useForm();
	const [createOrder] = useCreateInternalOrderMutation();
	const [uploadInternalFiles] = useUploadInternalFilesMutation();
	const [reanalyzeInternalFile] = useReanalyzeInternalFileMutation();
	const [finalizeInternalOrder, { isLoading: isFinalizing }] =
		useFinalizeInternalOrderMutation();

	const [submitting, setSubmitting] = useState(false);
	const [orderId, setOrderId] = useState<string | null>(null);
	const [orderName, setOrderName] = useState<string>('');

	// Phase 2: the order exists as a draft, so poll its line items (the `order`
	// param is what makes the backend include draft orders) until every file
	// has been analyzed.
	const { data, isLoading: isLoadingItems } = useGetInternalOrderLineItemsQuery(
		orderId as string,
		{ skip: !orderId, pollingInterval: 3000 },
	);
	const lineItems = data?.results ?? [];
	const analyzed = analyzedCount(lineItems);
	const failedCount = lineItems.filter(
		(li) => li.internal_file_analysis_status === 'failed',
	).length;
	const canFinalize = finalizeEnabled(lineItems);
	// Empty string = nothing to explain, so no tooltip is rendered at all (AntD
	// keeps an empty-title Tooltip closed).
	const finalizeHint = canFinalize
		? ''
		: lineItems.length === 0
			? 'This order has no line items yet.'
			: failedCount > 0
				? 'Retry the failed analyses first — every line item has to finish analysis before the order can be finalized.'
				: `Waiting on file analysis — ${analyzed} of ${lineItems.length} line items analyzed.`;

	const onFinish = async () => {
		setSubmitting(true);
		try {
			const values = await form.validateFields();
			const rows: InternalOrderLineItemFormValues[] = values.line_items ?? [];

			const payload = buildInternalOrderPayload({ ...values, line_items: rows });
			const result = await createOrder(payload).unwrap();

			setOrderId(String(result.id));
			setOrderName(result.name);
			message.success(`Internal order ${result.name} created`);

			const created = matchCreatedLineItems(rows, result.line_items ?? []);
			const failed: string[] = [];
			for (let i = 0; i < rows.length; i += 1) {
				const target = created[i];
				if (!target) {
					// No created item could be paired with this row: dropping its
					// files silently would leave a draft that can never finalize.
					failed.push(rows[i].name);
					continue;
				}
				const stepFile = extractUploadFile(rows[i].step_file);
				const drawingFile = extractUploadFile(rows[i].drawing_file);
				if (!stepFile && !drawingFile) continue;
				const formData = new FormData();
				if (stepFile)
					formData.append('internal_step_file', stepFile, stepFile.name);
				if (drawingFile)
					formData.append('internal_drawing_file', drawingFile, drawingFile.name);
				try {
					// eslint-disable-next-line no-await-in-loop
					await uploadInternalFiles({
						id: String(target.id),
						formData,
					}).unwrap();
				} catch (uploadError) {
					console.error('Failed to upload internal files:', uploadError);
					failed.push(target.name);
				}
			}
			if (failed.length > 0) {
				message.error(
					`Files could not be uploaded for: ${failed.join(', ')}. Upload them again from the order page.`,
				);
			}
		} catch (error) {
			console.error('Failed to create internal order:', error);
			if ((error as { errorFields?: unknown[] })?.errorFields === undefined) {
				// Permission rejections carry a human-readable detail (e.g. the
				// Shop Managers guard) -- show it instead of a generic failure
				// so a user without the role isn't left guessing (PTB-6480).
				const detail = (error as { data?: { detail?: string } })?.data?.detail;
				message.error(detail || 'Failed to create internal order');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const onRetry = async (id: string) => {
		try {
			await reanalyzeInternalFile(id).unwrap();
			message.success('Analysis restarted');
		} catch (error) {
			console.error('Failed to restart analysis:', error);
			message.error('Failed to restart analysis');
		}
	};

	const onFinalize = async () => {
		if (!orderId) return;
		try {
			await finalizeInternalOrder(orderId).unwrap();
			message.success('Internal order finalized');
			navigate(`/orders/${orderId}`);
		} catch (error) {
			console.error('Failed to finalize internal order:', error);
			message.error(notReadyMessage(error) ?? 'Failed to finalize the order');
		}
	};

	if (isLoadingUser) return <Spin />;

	if (!canManage) {
		return (
			<Result
				status="403"
				title="403"
				subTitle="Sorry, you are not authorized to create internal orders. This requires Shop Manager access."
				extra={
					<Button type="primary" onClick={() => navigate('/orders')}>
						Back to Orders
					</Button>
				}
			/>
		);
	}

	const finalizeButton = (
		<Button
			type="primary"
			size="large"
			loading={isFinalizing}
			disabled={!canFinalize}
			// Lets the wrapping span below receive the hover a disabled button
			// would otherwise swallow, so the "what's missing" tooltip shows.
			style={canFinalize ? undefined : { pointerEvents: 'none' }}
			onClick={onFinalize}
		>
			Finalize
		</Button>
	);

	return (
		<main data-testid="page-fastlane-internal-order-create" data-ready="true">
			<div className="flex flex-column justify-between py-4">
				<h1 className="text-2xl font-bold m-0">Create Internal Order</h1>
				<p className="text-gray-500 mt-1 mb-0">
					For tooling, fixtures, R&D, rework, maintenance, and other non-customer work.
				</p>
			</div>
			<Card className="shadow-sm">
				<Steps
					size="small"
					style={{ marginBottom: 24 }}
					current={orderId ? 1 : 0}
					items={[{ title: 'Details & Files' }, { title: 'File Analysis' }]}
				/>
				{orderId ? (
					<Flex
						vertical
						gap={16}
						data-testid="fastlane-internal-order-draft"
						data-order-id={orderId}
						data-order-name={orderName}
					>
						<Flex
							align="flex-start"
							justify="space-between"
							gap={16}
							wrap="wrap"
						>
							<div>
								<Space align="center">
									<Title level={4} style={{ marginBottom: 0 }}>
										{orderName}
									</Title>
									<Tag color="orange">Draft</Tag>
								</Space>
								<div className="text-gray-500 mt-1">
									Analyzing the uploaded 3D files. This can take a few minutes —
									you can leave and come back to the order at any time.
								</div>
							</div>
							<Space>
								<Button
									size="large"
									data-testid="fastlane-internal-order-continue-later-btn"
									onClick={() => navigate(`/orders/${orderId}`)}
								>
									Continue later
								</Button>
								{finalizeHint ? (
									<Tooltip title={finalizeHint}>
										<span className="inline-block cursor-not-allowed">
											{finalizeButton}
										</span>
									</Tooltip>
								) : (
									finalizeButton
								)}
							</Space>
						</Flex>

						<div>
							<Flex justify="space-between" align="center" gap={8}>
								<Text type="secondary">File analysis</Text>
								<Text type="secondary">
									{analyzed} of {lineItems.length} analyzed
								</Text>
							</Flex>
							<Progress
								showInfo={false}
								percent={
									lineItems.length
										? Math.round((analyzed / lineItems.length) * 100)
										: 0
								}
								status={
									failedCount > 0
										? 'exception'
										: canFinalize
											? 'success'
											: 'active'
								}
							/>
						</div>

						{isLoadingItems ? (
							<Spin />
						) : (
							<AnalysisRows lineItems={lineItems} onRetry={onRetry} />
						)}
					</Flex>
				) : (
					<Form
						form={form}
						onFinish={onFinish}
						layout="vertical"
						autoComplete="off"
						requiredMark="optional"
						data-testid="fastlane-internal-order-form"
					>
						<Card
							type="inner"
							size="small"
							title="Order Information"
							style={{ marginBottom: 16 }}
						>
							<Row gutter={[16, 0]}>
								<Col xs={24} md={8}>
									<Form.Item
										name="internal_category"
										label="Category"
										style={{ marginBottom: 0 }}
										rules={[{ required: true, message: 'Select a category' }]}
									>
									<Select
										data-testid="fastlane-internal-order-category-select"
										placeholder="Select category"
											options={INTERNAL_CATEGORIES}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} md={16}>
									<Form.Item
										name="internal_notes"
										label="Internal Notes"
										style={{ marginBottom: 0 }}
									>
										<TextArea
											data-testid="fastlane-internal-order-notes-input"
											rows={2}
											placeholder="Context for the shop — what this work is for, who asked for it, anything special"
										/>
									</Form.Item>
								</Col>
							</Row>
						</Card>

						<Card
							type="inner"
							size="small"
							title="Line Items"
							style={{ marginBottom: 16 }}
						>
							<Form.List name="line_items">
								{(fields, { add, remove }) => (
									<>
										{fields.length === 0 && (
											<Text
												type="secondary"
												style={{ display: 'block', marginBottom: 12 }}
											>
												No line items yet — add at least one to create this
												order.
											</Text>
										)}
										{/*
										 * One Card per line item, holding every field of that
										 * item: the e2e page object scopes an item as the
										 * `.ant-card` containing a `line_items_<index>_*`
										 * control, so all of an index's Form.Items must stay in
										 * its Card and each label must identify exactly one
										 * Form.Item there. The grid inside is split into themed
										 * Rows (name/qty, material/finish/coating, dates,
										 * uploads).
										 */}
										{fields.map(({ key, name, ...restField }, index) => (
											<Card
												key={key}
												size="small"
												className="internal-line-item-card"
												style={{ marginBottom: 12 }}
											>
												{/*
												 * The item header lives in the card body, not in the
												 * card's own `title`/`extra`: `src/index.css`
												 * force-stacks and centers every
												 * `.ant-card-head-wrapper` below 1440px, which would
												 * drop the remove button onto its own centered line.
												 */}
												<Flex
													align="center"
													justify="space-between"
													gap={8}
													style={{ marginBottom: 12 }}
												>
													<Text
														type="secondary"
														strong
														style={{
															fontSize: 12,
															letterSpacing: 0.4,
															textTransform: 'uppercase',
														}}
													>
														Item {index + 1}
													</Text>
													<Button
														type="text"
														danger
														size="small"
														aria-label={`Remove item ${index + 1}`}
														icon={<MinusCircleOutlined />}
														onClick={() => remove(name)}
													/>
												</Flex>
												<Row gutter={[16, 0]}>
													<Col xs={24} md={18}>
														<Form.Item
															{...restField}
															name={[name, 'name']}
															label="Part Name"
															rules={[{ required: true, message: 'Required' }]}
														>
															<Input placeholder="e.g. Soft Jaw Set - OP1" />
														</Form.Item>
													</Col>
													<Col xs={24} md={6}>
														<Form.Item
															{...restField}
															name={[name, 'quantity']}
															label="Qty"
															initialValue={1}
															rules={[{ required: true, message: 'Qty' }]}
														>
															<InputNumber min={1} style={{ width: '100%' }} />
														</Form.Item>
													</Col>
												</Row>
												<Row gutter={[16, 0]}>
													<Col xs={24} md={8}>
														<Form.Item
															{...restField}
															name={[name, 'material_name']}
															label="Material"
															rules={[{ required: true, message: 'Required' }]}
														>
															<AutoComplete
																placeholder="Select or type material"
																options={materialTypes.map((t) => ({
																	value: t.display_name,
																}))}
																filterOption={(input, option) =>
																	(option?.value ?? '')
																		.toLowerCase()
																		.includes(input.toLowerCase())
																}
															/>
														</Form.Item>
													</Col>
													<Col xs={24} md={8}>
														<Form.Item
															{...restField}
															name={[name, 'finish_name']}
															label="Surface Finish"
															rules={[{ required: true, message: 'Required' }]}
														>
															<Input placeholder="e.g. Anodize" />
														</Form.Item>
													</Col>
													<Col xs={24} md={8}>
														<Form.Item
															{...restField}
															name={[name, 'coating_name']}
															label="Coating"
															rules={[{ required: true, message: 'Required' }]}
														>
															<Input placeholder="e.g. None" />
														</Form.Item>
													</Col>
												</Row>
												<Row gutter={[16, 0]}>
													<Col xs={24} md={12}>
														<Form.Item
															{...restField}
															name={[name, 'required_partner_ship_date']}
															label="Required Shop Ship Date"
															extra="When the shop needs to ship it"
															rules={[{ required: true, message: 'Required' }]}
														>
															<DatePicker className="w-full" />
														</Form.Item>
													</Col>
													<Col xs={24} md={12}>
														<Form.Item
															{...restField}
															name={[name, 'required_part_ship_date']}
															label="Required Part Ship Date"
															extra="When the part is due"
															rules={[{ required: true, message: 'Required' }]}
														>
															<DatePicker className="w-full" />
														</Form.Item>
													</Col>
												</Row>
												<Row gutter={[16, 0]}>
													<Col xs={24} md={12}>
														<Form.Item
															{...restField}
															name={[name, 'step_file']}
															label="STEP File"
															extra="3D model (.step or .stp) — analyzed automatically"
															valuePropName="fileList"
															getValueFromEvent={normalizeUploadEvent}
															style={{ marginBottom: 0 }}
															rules={[
																{ required: true, type: 'array', message: 'Required' },
															]}
														>
															<Upload
																maxCount={1}
																accept=".step,.stp"
																beforeUpload={() => false}
															>
																<Button icon={<UploadOutlined />}>Upload</Button>
															</Upload>
														</Form.Item>
													</Col>
													<Col xs={24} md={12}>
														<Form.Item
															{...restField}
															name={[name, 'drawing_file']}
															label="Drawing"
															extra="Print for the floor (.pdf, .png or .jpg)"
															valuePropName="fileList"
															getValueFromEvent={normalizeUploadEvent}
															style={{ marginBottom: 0 }}
															rules={[
																{ required: true, type: 'array', message: 'Required' },
															]}
														>
															<Upload
																maxCount={1}
																accept=".pdf,.png,.jpg"
																beforeUpload={() => false}
															>
																<Button icon={<UploadOutlined />}>Upload</Button>
															</Upload>
														</Form.Item>
													</Col>
												</Row>
											</Card>
										))}
										<Button
											type="dashed"
											onClick={() => add()}
											block
											icon={<PlusOutlined />}
										>
											Add Line Item
										</Button>
									</>
								)}
							</Form.List>
						</Card>

						<Flex justify="flex-end">
								<Button
									type="primary"
									size="large"
									htmlType="submit"
									loading={submitting}
									data-testid="fastlane-internal-order-create-btn"
							>
								Create Internal Order
							</Button>
						</Flex>
					</Form>
				)}
			</Card>
		</main>
	);
};

export default AddInternalOrder;
